import { AppDispatch } from 'common/redux/core/store.config';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
    CreateRoleDto,
    EditRoleDto,
    FeatureAccessWithOptionalSubfeatures,
    RoleDto,
    RoleDtoWithAcces,
    RoleFeature,
    RoleInfo,
    UserRoleInfo
} from '../models/role-management.model';
import { DEFAULT_ERROR_MESSAGE, FAILURE_HEADER, SUCCESS_HEADER } from 'common/config';
import { MessageType } from 'common/enums';
import { appStateActions } from 'common/redux/app-state';
import { RoleManagementService } from '../redux/role-management.service';
import {
    DUPLICATE_ROLENAME,
    ROLE_CREATE_FAILED,
    ROLE_CREATE_SUCCESS,
    ROLE_DELETE_FAILED,
    ROLE_DELETE_FAILED_WHEN_USERS_MAPPED,
    ROLE_DELETE_SUCCESS,
    ROLE_EDIT_FAIL,
    ROLE_EDIT_SUCCESS
} from 'common/config/message.config';
import { getFlatRows } from './role-management-features.utils';

export const getAllRoles = async (
    dispatch: AppDispatch,
    setAllRolesData: Dispatch<SetStateAction<RoleDtoWithAcces[]>>,
    editData: UserRoleInfo | null = null
) => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const rawResp = await RoleManagementService.getAllRoles();
    if (rawResp?.errors && rawResp?.hasErrors) {
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
        /** need to show failed pop up here */
        dispatch({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_ERROR_MESSAGE,
                type: MessageType.Error
            }
        });
    } else if (rawResp?.value) {
        /**to show checkboxes adding access here */
        const modifiedData = getData(rawResp?.value, editData);
        setAllRolesData(modifiedData);
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};
const getFlattenedData = (
    data: FeatureAccessWithOptionalSubfeatures[],
    levelxyz: number,
    parentIdArray: string[]
): FeatureAccessWithOptionalSubfeatures[] => {
    return data
        .map((item) => {
            const newElement: FeatureAccessWithOptionalSubfeatures = {
                ...item,
                parentIdArray: parentIdArray,
                readAccess: item.readAccess,
                writeAccess: item.writeAccess
            };
            if (item.subFeatures.length > 0) {
                return [
                    newElement,
                    ...getFlattenedData(item.subFeatures, levelxyz + 1, [
                        ...parentIdArray,
                        item.applicationFeatureId
                    ])
                ];
            }
            return newElement;
        })
        .flat(Infinity) as unknown as FeatureAccessWithOptionalSubfeatures[];
};
export const getAllApplicationFeatures = async (
    roleId: string,
    dispatch: AppDispatch,
    setAllFeaturesData: Dispatch<SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>,
    backupData: MutableRefObject<RoleFeature[]>,
    flattenedData: MutableRefObject<RoleFeature[]>
) => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const rawResp = await RoleManagementService.getAllFeatures(roleId);
    if (rawResp?.errors && rawResp?.hasErrors) {
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
        /** need to show failed pop up here */
        dispatch({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_ERROR_MESSAGE,
                type: MessageType.Error
            }
        });
    } else {
        setAllFeaturesData(
            getFlatRows((rawResp?.value as FeatureAccessWithOptionalSubfeatures[]) ?? [], 0, [], {
                current: []
            })
        );
        backupData.current = rawResp?.value ?? [];
        flattenedData.current = getFlattenedData(
            (rawResp?.value as FeatureAccessWithOptionalSubfeatures[]) ?? [],
            0,
            []
        );
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

export const deleteRole = async (
    applicationRoleId: string,
    dispatch: AppDispatch,
    onDeleteSuccess: (isDeleted: boolean) => void
) => {
    dispatch({ type: appStateActions.showLoader.type, payload: true });

    const rawResp = await RoleManagementService.deleteRole(applicationRoleId);

    if (rawResp?.errors && rawResp.hasErrors) {
        handleDeleteError(dispatch);
    } else {
        /**
         * condition1:{isDeleted:true,isRoleMappedToUsers:false} --> successful deletion
         * condition2:{isDeleted:false,isRoleMappedToUsers:true} ---> roleMapped to features
         * condition3:{isDeleted:false,isRoleMappedToUsers:false} --> api error or other error
         *  */
        if (rawResp?.value && rawResp?.value?.isDeleted && !rawResp?.value?.isRoleMappedToUsers) {
            onDeleteSuccess(true);
            handleDeleteSuccess(dispatch);
        } else if (
            rawResp?.value &&
            !rawResp?.value?.isDeleted &&
            rawResp?.value?.isRoleMappedToUsers
        ) {
            handleRoleMappedToFeature(dispatch);
        } else {
            handleDeleteError(dispatch);
        }
    }
    dispatch({ type: appStateActions.showLoader.type, payload: false });
};

const handleDeleteError = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: FAILURE_HEADER,
            message: ROLE_DELETE_FAILED,
            type: MessageType.Error
        }
    });
};

const handleDeleteSuccess = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: SUCCESS_HEADER,
            message: ROLE_DELETE_SUCCESS,
            type: MessageType.Success
        }
    });
};

const handleRoleMappedToFeature = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: FAILURE_HEADER,
            message: ROLE_DELETE_FAILED_WHEN_USERS_MAPPED,
            type: MessageType.Error
        }
    });
};

export const updateRole = async (
    data: EditRoleDto,
    dispatch: AppDispatch,
    onSuccess: () => void
) => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const rawResp = await RoleManagementService.updateRole(data);
    if (rawResp?.errors && rawResp?.hasErrors) {
        handleEditRoleFailed(dispatch);
    } else {
        if (
            rawResp?.value &&
            rawResp?.value?.isCreatedOrEdited &&
            !rawResp?.value?.isRoleNameAlreadyExists
        ) {
            handleEditRoleSuccess(dispatch);
            onSuccess();
        } else if (
            rawResp?.value &&
            !rawResp?.value?.isCreatedOrEdited &&
            rawResp?.value?.isRoleNameAlreadyExists
        ) {
            handleRoleNameAlreadyExists(dispatch);
        } else {
            handleEditRoleFailed(dispatch);
        }
    }
    dispatch({
        type: appStateActions.showLoader.type,
        payload: false
    });
};

export const addNewRole = async (
    data: CreateRoleDto,
    dispatch: AppDispatch,
    onSuccess: () => void
) => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const rawResp = await RoleManagementService.createRole(data);
    if (rawResp?.errors && rawResp?.hasErrors) {
        /** show failed pop up here */
        handleCreateRoleError(dispatch);
    } else {
        if (
            rawResp?.value &&
            rawResp?.value?.isCreatedOrEdited &&
            !rawResp?.value?.isRoleNameAlreadyExists
        ) {
            handleCreateRoleSuccess(dispatch);
            onSuccess();
        } else if (
            rawResp?.value &&
            !rawResp?.value?.isCreatedOrEdited &&
            rawResp?.value?.isRoleNameAlreadyExists
        ) {
            handleRoleNameAlreadyExists(dispatch);
        } else {
            handleCreateRoleError(dispatch);
        }
    }
    dispatch({
        type: appStateActions.showLoader.type,
        payload: false
    });
};

const handleEditRoleSuccess = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: SUCCESS_HEADER,
            message: ROLE_EDIT_SUCCESS,
            type: MessageType.Success
        }
    });
};
const handleEditRoleFailed = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: FAILURE_HEADER,
            message: ROLE_EDIT_FAIL,
            type: MessageType.Error
        }
    });
};
const handleCreateRoleSuccess = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: SUCCESS_HEADER,
            message: ROLE_CREATE_SUCCESS,
            type: MessageType.Success
        }
    });
};

const handleCreateRoleError = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: FAILURE_HEADER,
            message: ROLE_CREATE_FAILED,
            type: MessageType.Error
        }
    });
};

const handleRoleNameAlreadyExists = (dispatch: AppDispatch) => {
    dispatch({
        type: appStateActions.showMessageModal.type,
        payload: {
            isVisible: true,
            header: FAILURE_HEADER,
            message: DUPLICATE_ROLENAME,
            type: MessageType.Error
        }
    });
};

const getData = (allRolesData: RoleDto[], editData: UserRoleInfo | null): RoleDtoWithAcces[] => {
    /**to show checkboxes adding access here */
    const rolesData = allRolesData ?? [];
    if (editData) {
        const roleIDArray = editData.applicationRoles.map(
            (ele: RoleInfo) => ele.applicationRoleUserId
        );
        return rolesData.map((ele: RoleDto) => {
            if (roleIDArray.includes(ele.applicationRoleId)) {
                return { ...ele, access: true };
            }
            return { ...ele, access: false };
        });
    }
    return rolesData.map((ele: RoleDto) => {
        return { ...ele, access: false };
    });
};
